import { Component, Input, OnDestroy, OnInit } from '@angular/core';
import { Subscription, take, finalize } from 'rxjs';
import { RewardService } from 'src/app/core/api/reward/reward.service';
import { UserService } from 'src/app/core/api/user/user.service';
import { CouponStatus } from 'src/app/shared/models/constants/coupon-status';
import { BusinessSummaryResponseDTO } from 'src/app/shared/models/dtos/responses/business-summary-response-dto/business-summary-response-dto';
import { CouponVip } from 'src/app/shared/models/dtos/responses/coupon-vip/coupon-vip';
import { Coupon } from 'src/app/shared/models/dtos/responses/coupon/coupon';
import { dedup } from 'src/app/utils/list';
import { ChipContent } from 'src/app/shared/models/chip-content/chip-content';
import {
  MultiSelectChip,
  generateSeriesChips,
} from '../filter-container/filter-container.component';
import { FilterOptions } from 'src/app/shared/models/filters/filter-options';
import { FilterType } from 'src/app/shared/models/constants/filter-type';
import { FamilyDTO } from 'src/app/shared/models/dtos/responses/family-dto/family-dto';
import { ProductService } from 'src/app/core/api/product/product.service';

@Component({
  selector: 'app-customer-detail-coupons',
  templateUrl: './customer-detail-coupons.component.html',
  styleUrls: ['./customer-detail-coupons.component.scss'],
})
/**
 * Customer Detail Coupons component
 */
export class CustomerDetailCouponsComponent implements OnInit, OnDestroy {
  @Input() businessId!: number;
  businessSummary?: BusinessSummaryResponseDTO;
  loadingSummary = true;
  loadingVip = true;
  pullingCoupons = false;
  coupons: Coupon[] = [];
  private page = 0;
  private pageSize = 20;
  private readonly subsink = new Subscription();

  cardVips?: [CouponVip];

  allFamiliesWithEquipments: any;

  statusChips = ChipContent.StatusCoupon();
  chipContents: ChipContent[] = [];
  seriesChips: MultiSelectChip[] = [];
  filters: FilterOptions = {
    dateRange: { startDate: undefined, endDate: undefined },
    series: [],
    status: [],
  };

  /**
   * @constructor
   */
  constructor(
    private readonly userService: UserService,
    private readonly rewardService: RewardService,
    private readonly productService: ProductService
  ) {
    const dateChip = new ChipContent(
      undefined,
      'dateRange',
      FilterType.DATE_RANGE,
      false
    );

    this.chipContents.push(dateChip);
  }

  /**
   * on init
   */
  ngOnInit(): void {
    this.getPrintersFamily();
    this.subsink.add(
      this.userService
        .getBusinessSummary(this.businessId)
        .pipe(
          finalize(() => {
            this.loadingSummary = false;
          })
        )
        .subscribe((summary) => {
          this.businessSummary = summary;
          console.log(this.businessSummary);
        })
    );
    this.pullCoupons();

    this.subsink.add(
      this.userService.getCouponVipByBusinessId(this.businessId).subscribe({
        next: (res: [CouponVip]) => {
          this.loadingVip = false;
          this.cardVips = res;
          for (const coupon of res) {
            coupon.status = CouponStatus.ACTIVE;
          }
        },
        error: () => {
          this.loadingVip = false;
        },
      })
    );
  }

  /**
   * clean up resources
   */
  ngOnDestroy() {
    this.subsink.unsubscribe();
  }

  /**
   * Pulls more coupons
   */
  pullCoupons() {
    if (this.pullingCoupons) {
      return;
    }
    const params = this.prepareParams();
    this.pullingCoupons = true;
    this.subsink.add(
      this.rewardService
        .getCoupons(this.businessId, params)
        .pipe(
          take(1),
          finalize(() => {
            this.pullingCoupons = false;
          })
        )
        .subscribe((coupons) => {
          console.log(coupons);
          if (coupons.length === 0) {
            return;
          }
          this.coupons = dedup(
            this.coupons.concat(coupons),
            (coupon) => coupon.id
          );
          if (coupons.length === this.pageSize) {
            this.page++;
          }
          this.coupons.sort((a, b) => {
            const x = a.displayDate!;
            const y = b.displayDate!;

            return (x < y && 1) || (x > y && -1) || 0;
          });
        })
    );
  }

  /**
   * Updates Filters
   * @param {AdminFilters} filters
   */
  updateFilter(filters: any) {
    this.filters = filters;
    this.page = 0;
    this.coupons = [];
    this.pullCoupons();
  }

  /**
   * Captures all the families from registered equipment
   */
  getPrintersFamily() {
    this.productService
      .getBusinessEquipments(this.businessId)
      .subscribe((equipments) => {
        const families = equipments
          .map((e) => e.familyResponseDTO)
          .filter((f): f is FamilyDTO => !!f);

        const uniqueFamilies = families.filter(
          (family, index, self) =>
            self.findIndex((f) => f.id === family.id) === index
        );
        this.seriesChips = generateSeriesChips(uniqueFamilies);
      });
  }

  /**
   * @param {number} familyId
   * @return {boolean} indicates if the timeline is disabled
   */
  isTimelineDisabled(familyId: number): boolean {
    for (const activeLevel of this.businessSummary!.activeBusinessLevels!) {
      if (activeLevel.familyPk === familyId) {
        return false;
      }
    }
    return true;
  }

  /**
   * Prepares search params for business list page
   * @return {Object}
   */
  private prepareParams() {
    const { startDate, endDate } = this.filters.dateRange || {};
    let params = {
      page: this.page,
      pageSize: this.pageSize,
      series: this.filters.series,
      status: this.filters.status,
      ...(startDate && { startDate }),
      ...(endDate && { endDate }),
    };
    if (this.filters.status?.length === 0) {
      params = {
        ...params,
        status: [
          CouponStatus.ACTIVE,
          CouponStatus.REDEEMED,
          CouponStatus.EXPIRED,
          CouponStatus.CANCELED,
        ],
      };
    }
    return params;
  }
}
